import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  Save, 
  Send,
  ArrowLeft,
  ArrowRight,
  Flag
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAssessmentStore } from '../../stores/assessmentStore';
import { useAuthStore } from '../../stores/authStore';
import MathField from '../math/MathField';
import MathRenderer from '../math/MathRenderer';

const AssessmentTaking = ({ assessmentId, onComplete }) => {
  const { user } = useAuthStore();
  const { currentAssessment, loadAssessment } = useAssessmentStore();
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved');

  useEffect(() => {
    if (assessmentId) {
      loadAssessment(assessmentId);
    }
  }, [assessmentId, loadAssessment]);

  // Timer effect
  useEffect(() => {
    if (currentAssessment?.timeLimit && timeRemaining === null) {
      setTimeRemaining(currentAssessment.timeLimit * 60); // Convert minutes to seconds
    }

    if (timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmit(); // Auto-submit when time runs out
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [timeRemaining, currentAssessment]);

  // Auto-save effect
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      handleAutoSave();
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [answers]);

  const handleAutoSave = async () => {
    setAutoSaveStatus('saving');
    // Implement auto-save logic here
    setTimeout(() => {
      setAutoSaveStatus('saved');
    }, 1000);
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    setAutoSaveStatus('unsaved');
  };

  const toggleFlag = (questionIndex) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionIndex)) {
        newSet.delete(questionIndex);
      } else {
        newSet.add(questionIndex);
      }
      return newSet;
    });
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Submit assessment logic here
      console.log('Submitting assessment with answers:', answers);
      onComplete?.(answers);
    } catch (error) {
      console.error('Error submitting assessment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getAnsweredQuestions = () => {
    return Object.keys(answers).filter(questionId => {
      const answer = answers[questionId];
      return answer !== undefined && answer !== '' && answer !== null;
    }).length;
  };

  if (!currentAssessment) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sigma-600"></div>
      </div>
    );
  }

  const questions = currentAssessment.questions || [];
  const currentQuestion = questions[currentQuestionIndex];

  if (questions.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Questions Available</h3>
        <p className="text-gray-500">This assessment doesn't have any questions yet.</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar - Question Navigation */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        {/* Assessment Info */}
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {currentAssessment.title}
          </h2>
          
          {/* Timer */}
          {timeRemaining !== null && (
            <div className={clsx(
              'flex items-center mb-2',
              timeRemaining < 300 ? 'text-red-600' : 'text-gray-600'
            )}>
              <Clock className="w-4 h-4 mr-2" />
              <span className="font-mono">
                Time: {formatTime(timeRemaining)}
              </span>
            </div>
          )}

          {/* Progress */}
          <div className="text-sm text-gray-600">
            <div className="flex items-center justify-between mb-1">
              <span>Progress</span>
              <span>{getAnsweredQuestions()}/{questions.length}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-sigma-600 h-2 rounded-full transition-all"
                style={{
                  width: `${(getAnsweredQuestions() / questions.length) * 100}%`
                }}
              />
            </div>
          </div>

          {/* Auto-save Status */}
          <div className="flex items-center mt-2 text-xs">
            <div className={clsx(
              'w-2 h-2 rounded-full mr-2',
              autoSaveStatus === 'saved' ? 'bg-green-500' :
              autoSaveStatus === 'saving' ? 'bg-yellow-500' : 'bg-gray-400'
            )} />
            <span className="text-gray-500">
              {autoSaveStatus === 'saved' ? 'All changes saved' :
               autoSaveStatus === 'saving' ? 'Saving...' : 'Unsaved changes'}
            </span>
          </div>
        </div>

        {/* Question Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-5 gap-2">
            {questions.map((question, index) => {
              const isAnswered = answers[question.id] !== undefined && answers[question.id] !== '';
              const isFlagged = flaggedQuestions.has(index);
              const isCurrent = index === currentQuestionIndex;

              return (
                <button
                  key={question.id}
                  onClick={() => setCurrentQuestionIndex(index)}
                  className={clsx(
                    'relative w-12 h-12 rounded-lg border-2 text-sm font-medium transition-all',
                    isCurrent 
                      ? 'border-sigma-500 bg-sigma-100 text-sigma-700'
                      : isAnswered
                      ? 'border-green-300 bg-green-100 text-green-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  )}
                >
                  {index + 1}
                  {isFlagged && (
                    <Flag className="absolute -top-1 -right-1 w-3 h-3 text-red-500 fill-current" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Submit Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full bg-sigma-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-sigma-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Assessment
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content - Current Question */}
      <div className="flex-1 flex flex-col">
        {/* Question Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h3 className="text-lg font-medium text-gray-900">
                Question {currentQuestionIndex + 1} of {questions.length}
              </h3>
              <span className="text-sm text-gray-500">
                {currentQuestion.points} {currentQuestion.points === 1 ? 'point' : 'points'}
              </span>
            </div>
            
            <button
              onClick={() => toggleFlag(currentQuestionIndex)}
              className={clsx(
                'p-2 rounded-lg transition-colors',
                flaggedQuestions.has(currentQuestionIndex)
                  ? 'text-red-600 bg-red-50 hover:bg-red-100'
                  : 'text-gray-400 hover:text-red-500 hover:bg-gray-100'
              )}
              title="Flag question"
            >
              <Flag className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Question Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl">
            {/* Question Title */}
            <h4 className="text-xl font-semibold text-gray-900 mb-4">
              {currentQuestion.title}
            </h4>

            {/* Question Content */}
            {currentQuestion.type === 'math' && currentQuestion.mathContent ? (
              <div className="mb-6 p-6 bg-gray-50 rounded-lg text-center">
                <MathRenderer latex={currentQuestion.mathContent} />
              </div>
            ) : currentQuestion.content && (
              <div className="mb-6 text-gray-700 whitespace-pre-wrap">
                {currentQuestion.content}
              </div>
            )}

            {/* Answer Interface */}
            <div className="space-y-4">
              {currentQuestion.type === 'text' && (
                <textarea
                  value={answers[currentQuestion.id] || ''}
                  onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                  placeholder="Type your answer here..."
                />
              )}

              {currentQuestion.type === 'math' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Answer:
                  </label>
                  <MathField
                    value={answers[currentQuestion.id] || ''}
                    onChange={(latex) => handleAnswerChange(currentQuestion.id, latex)}
                    placeholder="Enter your mathematical answer..."
                    className="w-full"
                  />
                  {answers[currentQuestion.id] && (
                    <div className="mt-3 p-3 bg-gray-50 rounded border">
                      <div className="text-xs text-gray-500 mb-2">Preview:</div>
                      <MathRenderer latex={answers[currentQuestion.id]} />
                    </div>
                  )}
                </div>
              )}

              {currentQuestion.type === 'multiple-choice' && (
                <div className="space-y-3">
                  {currentQuestion.options?.map((option, index) => (
                    <label
                      key={index}
                      className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name={`question-${currentQuestion.id}`}
                        value={index}
                        checked={answers[currentQuestion.id] == index}
                        onChange={(e) => handleAnswerChange(currentQuestion.id, parseInt(e.target.value))}
                        className="w-4 h-4 text-sigma-600 border-gray-300 focus:ring-sigma-500"
                      />
                      <span className="ml-3 font-medium text-gray-700">
                        {String.fromCharCode(65 + index)}.
                      </span>
                      <span className="ml-2 text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {currentQuestion.type === 'true-false' && (
                <div className="space-y-3">
                  {['true', 'false'].map((option) => (
                    <label
                      key={option}
                      className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name={`question-${currentQuestion.id}`}
                        value={option}
                        checked={answers[currentQuestion.id] === option}
                        onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                        className="w-4 h-4 text-sigma-600 border-gray-300 focus:ring-sigma-500"
                      />
                      <span className="ml-3 text-gray-700 capitalize">{option}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Footer */}
        <div className="bg-white border-t border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
              disabled={currentQuestionIndex === 0}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </button>

            <span className="text-sm text-gray-500">
              {currentQuestionIndex + 1} of {questions.length}
            </span>

            <button
              onClick={() => setCurrentQuestionIndex(Math.min(questions.length - 1, currentQuestionIndex + 1))}
              disabled={currentQuestionIndex === questions.length - 1}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentTaking;